import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const vendorApplicationSchema = z.object({
  companyName: z.string().min(1, 'Company name is required'),
  websiteUrl: z.string().url().optional().or(z.literal('')),
  contactName: z.string().min(1, 'Contact name is required'),
  contactEmail: z.string().email('Valid email is required'),
  contactPhone: z.string().optional(),
  statesServed: z.array(z.string()).min(1, 'At least one state must be selected'),
  employeeRange: z.string().optional(),
  notes: z.string().optional(),
  certifications: z.object({
    esacMember: z.boolean().optional(),
    irs5500Member: z.boolean().optional(),
    napeoMember: z.boolean().optional(),
  }).optional(),
});

export type VendorApplicationFormData = z.infer<typeof vendorApplicationSchema>;

export const useVendorApplicationForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  

  const form = useForm<VendorApplicationFormData>({
    resolver: zodResolver(vendorApplicationSchema),
    defaultValues: {
      companyName: '',
      websiteUrl: '',
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      statesServed: [],
      employeeRange: '',
      notes: '',
      certifications: {
        esacMember: false,
        irs5500Member: false,
        napeoMember: false,
      },
    },
  });

  const onSubmit = async (data: VendorApplicationFormData) => {
    setIsSubmitting(true);
    
    try {
      // Submit application
      const { data: result, error } = await supabase.functions.invoke('submit-vendor-application', {
        body: {
          ...data,
        },
      });

      if (error) {
        console.error('Supabase function error:', error);
        throw new Error(error.message || 'Failed to submit application');
      }

      if (!result.success) {
        throw new Error(result.error || 'Application submission failed');
      }

      setIsSubmitted(true);
      toast.success("Application submitted! Thank you for your interest. We'll review your application and get back to you soon.");

    } catch (error: any) {
      console.error('Application submission error:', error);
      toast.error(error.message || "Submission failed. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    form,
    isSubmitting,
    isSubmitted,
    onSubmit: form.handleSubmit(onSubmit),
  };
};